import { Button, type SelectOption } from '@ph-react-ui/core';
import { format } from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useGetMonthlyMenuByStudent } from '../../../hooks/payments/useGetMonthlyMenuByStudent';
import { useModal } from '../../../hooks/useModal';
import { Select } from '../../../utils/components/hoc-components';
import { SCHOOL_YEAR_START_MONTH } from '../../../utils/constants/food-management/schoolYearStartMonth';
import { PARTIAL_MONTHLY_MENU_PAYMENT } from '../../../utils/constants/payments/modals';
import { displayPrice } from '../../../utils/displayPrice';
import { Dialog } from '../../shared/components/Dialog';

export const PartialMonthlyMenuPaymentModal = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const studentId = searchParams.get('id');
  const studentName = searchParams.get('name');
  const startYearParam = searchParams.get('startYear');

  const { close: closeModal } = useModal(PARTIAL_MONTHLY_MENU_PAYMENT);

  const getCurrentSchoolYearStart = useCallback(() => {
    const today = new Date();
    const currentMonth = today.getMonth() + 1;
    const currentYear = today.getFullYear();

    return currentMonth >= SCHOOL_YEAR_START_MONTH
      ? currentYear
      : currentYear - 1;
  }, []);

  const selectedStartYear = useMemo(
    () =>
      startYearParam ? Number(startYearParam) : getCurrentSchoolYearStart(),
    [startYearParam, getCurrentSchoolYearStart]
  );

  const { data: monthlyMenuItems } = useGetMonthlyMenuByStudent(
    studentId ?? '',
    selectedStartYear
  );

  const [options, setOptions] = useState<SelectOption<string>[]>([]);

  const methods = useForm<{ selectedMonths: string[] }>({
    defaultValues: {
      selectedMonths: [],
    },
  });

  const unpaidItems = useMemo(() => {
    if (!monthlyMenuItems) {
      return [];
    }
    return monthlyMenuItems
      .filter((item) => !item.paidAt)
      .sort((a, b) => {
        if (a.year !== b.year) return a.year - b.year;
        return a.month - b.month;
      });
  }, [monthlyMenuItems]);

  useEffect(() => {
    if (unpaidItems.length === 0) {
      setOptions([]);
      return;
    }

    const firstUnpaidIndex = 0;

    const options = unpaidItems.map((item, index) => {
      const itemDate = new Date(item.year, item.month - 1);
      const monthName = t(`MONTHS.${format(itemDate, 'MMMM').toUpperCase()}`);
      const amount = displayPrice(
        item.amount,
        t('PAYMENTS.LABELS.BGN'),
        t('PAYMENTS.LABELS.EUR'),
        item.currency
      );

      return {
        label: `${monthName} ${item.year} - ${amount}`,
        value: item.mealMenuId,
        disabled: index !== firstUnpaidIndex,
      };
    });

    setOptions(options);
  }, [unpaidItems, t]);

  const selectedMonths = methods.watch('selectedMonths');

  const onSubmit = useCallback(() => {
    if (!studentId || !selectedMonths || selectedMonths.length === 0) {
      return;
    }

    const mealMenuIds = Array.isArray(selectedMonths)
      ? selectedMonths
      : [selectedMonths];

    const selectedItems = unpaidItems.filter((item) =>
      mealMenuIds.includes(item.mealMenuId)
    );

    const firstSelectedItem = selectedItems[0];
    if (!firstSelectedItem) return;

    const paymentItems = selectedItems.map((item) => ({
      mealMenuId: item.mealMenuId,
      month: item.month,
      year: item.year,
      amount: item.amount,
      currency: item.currency,
    }));

    closeModal();
    navigate(
      `/dashboard/payments/monthly-menu/pre-payment/${studentId}/${firstSelectedItem.month}/${firstSelectedItem.year}`,
      { state: { mealMenuIds, paymentItems } }
    );
  }, [studentId, selectedMonths, unpaidItems, closeModal, navigate]);

  useEffect(() => {
    setOptions((options) =>
      options.map((item, index) => {
        return {
          ...item,
          disabled: !(
            selectedMonths.length === index ||
            selectedMonths.length === index + 1
          ),
        };
      })
    );
  }, [selectedMonths.length]);

  const isSubmitDisabled = useMemo(() => {
    return !selectedMonths || selectedMonths.length === 0 || !monthlyMenuItems;
  }, [selectedMonths, monthlyMenuItems]);

  return (
    <Dialog
      className="dialog__monthly-menu__partial-payment"
      onClose={() => closeModal()}
      dialogHeader={t('PAYMENTS.LABELS.SELECT_PERIOD') ?? ''}
      actionButton={
        <Button
          disabled={isSubmitDisabled}
          type="submit"
          form="monthly-menu-payment-modal-form"
        >
          {t('ACTIONS.CONTINUE')}
        </Button>
      }
    >
      <FormProvider {...methods}>
        <form
          id="monthly-menu-payment-modal-form"
          onSubmit={methods.handleSubmit(onSubmit)}
        >
          <h3 className="mb-3">
            {t('PAYMENTS.DIALOGS.LABELS.GOING_TO_PAY')}
            <span className="dialog__confirm__content__bold">
              {studentName}
            </span>
          </h3>
          <h3 className="mb-6">{t('PAYMENTS.DIALOGS.LABELS.CHOOSE_MONTHS')}</h3>
          <Select
            placeholder={t('PAYMENTS.DIALOGS.LABELS.SELECT_MONTHS') ?? ''}
            multi={true}
            options={options}
            name="selectedMonths"
          />
        </form>
      </FormProvider>
    </Dialog>
  );
};
